import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

function SEO({ description, image, meta, title, type, url }) {
    const { site } = useStaticQuery(graphql`
        query {
            site {
                siteMetadata {
                    defaultImageURL
                    description
                    logoURL
                    organizationName
                    siteTitle
                    siteURL
                    support {
                        email
                        url
                    }
                }
            }
        }
    `);

    const schemaOrgJSONLD = [
        {
            '@context': 'http://schema.org',
            '@type': 'Organization',
            name: site.siteMetadata.organizationName,
            url: site.siteMetadata.siteURL,
            logo: site.siteMetadata.logoURL,
            sameAs: [
                /**
                 * URL(s) of a reference Web page that unambiguously indicates the
                 * item's identity. E.g. the URL of the item's Wikipedia page,
                 * Wikidata entry, or social media profiles.
                 * e.g.:
                 */
                // "https://twitter.com/basecamp",
                // "https://facebook.com/37signals",
            ],
            contactPoint: [
                {
                    '@type': 'ContactPoint',
                    contactType: 'customer support',
                    email: site.siteMetadata.support.email,
                    // `URL` allows this to handle either a relative or full URL
                    url: new URL(
                        site.siteMetadata.support.url,
                        site.siteMetadata.siteURL
                    ).href,
                },
            ],
        },
        {
            '@context': 'http://schema.org',
            '@type': 'WebSite',
            author: site.siteMetadata.organizationName,
            name: site.siteMetadata.siteTitle,
            url: site.siteMetadata.siteURL,
        },
    ];

    const metaDescription = description || site.siteMetadata.description;
    let metaImage;
    if (image) {
        // Expands relative URLs with `siteURL`
        // Does NOT handle subdomains, such as 'https://support.weave.com'
        metaImage = new URL(metaImage, site.siteMetadata.siteURL).href;
    } else {
        metaImage = site.siteMetadata.defaultImageURL;
    }
    // Expands relative URLs with `siteURL`
    const metaUrl = new URL(url, site.siteMetadata.siteURL).href;

    return (
        <Helmet
            htmlAttributes={{ lang: 'en' }}
            // `String.fromCharCode(160)` is non-breaking space, a.k.a `&nbsp;`
            // It prevents widows in the title
            title={`${
                title
                    ? `${title}${String.fromCharCode(
                          160
                      )}|${String.fromCharCode(160)}`
                    : ''
            }${site.siteMetadata.siteTitle}`}
        >
            {/* General tags */}
            <meta name="description" content={metaDescription} />
            <meta name="image" content={metaImage} />

            {/* Schema.org tags */}
            <script type="application/ld+json">
                {JSON.stringify(schemaOrgJSONLD)}
            </script>

            {/* OpenGraph tags */}
            <meta name="og:description" content={metaDescription} />
            <meta name="og:image" content={metaImage} />
            <meta name="og:locale" content="en_US" />
            <meta name="og:title" content={title} />
            <meta name="og:type" content={type} />
            {/* test for `url` prop because 404 Page doesn't have a useful URL */}
            {url ? <meta name="og:url" content={metaUrl} /> : ''}

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta
                name="twitter:creator"
                content={
                    site.siteMetadata.author ? site.siteMetadata.author : ''
                }
            />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:title" content={title} />

            {/* `meta` from `props` in case of any extra `<meta>` elements */}
            {meta}

            {/* Google's reCAPTCHA script */}
            <script src="https://www.google.com/recaptcha/api.js?render=6LcrzK4ZAAAAADyM2yyjEcZaZz7pAX1uvBmzmg8C"></script>
        </Helmet>
    );
}

SEO.defaultProps = {
    description: '',
    meta: [],
    type: 'website',
};

SEO.propTypes = {
    description: PropTypes.string,
    lang: PropTypes.string,
    meta: PropTypes.arrayOf(PropTypes.object),
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
    url: PropTypes.string.isRequired,
};

export default SEO;
